import { useTranslate } from '@/hooks'
import styled from 'styled-components'
import { Button, ReverLoading } from '@itsrever/design-system'
import ProductPreview from '@/components/ProductPreview'
import { ReturnsLineItem as ModelsLineItem } from '@itsrever/rever-api'
import { ReturnsExchangeOptionsResponse } from '@itsrever/rever-api'
import { useAppSelector } from '@/redux/hooks'

interface InitialProps {
    product: ModelsLineItem
    handleClickExchange: () => void
    handleClickRefund: () => void
    handleClickFullCatalog: () => void
    exchangeOptions: ReturnsExchangeOptionsResponse
    loading: boolean
}

export const Initial: React.FC<InitialProps> = ({
    product,
    handleClickExchange,
    handleClickRefund,
    handleClickFullCatalog,
    exchangeOptions,
    loading
}) => {
    const { translate } = useTranslate()

    const isAvailableForExchange =
        (exchangeOptions?.exchange_options?.exchange_flow ===
            'EXCHANGE_FLOW_OPEN' ||
            exchangeOptions.exchange_options?.exchange_flow ===
                'EXCHANGE_FLOW_OPEN_WITH_GIFT_CARD' ||
            exchangeOptions?.line_item_product_info?.variants?.some(
                (variant) => {
                    return (
                        variant.is_available &&
                        (!variant.is_inventory_enabled ||
                            (variant.inventory_quantity !== undefined &&
                                variant.inventory_quantity > 0))
                    )
                }
            )) ??
        false

    const isFullCatalog =
        exchangeOptions?.exchange_options?.exchange_flow ===
        'EXCHANGE_FLOW_FULL'

    const returnAvailable =
        exchangeOptions.exchange_options?.force_exchange !== true

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const isHoff = settings.ecommerce_group === 'hoff'
    const isMisako = settings.ecommerce_group === 'misako'

    if (loading)
        return (
            <div className="flex h-fit w-full items-center justify-center py-10">
                {isMisako ? null : <ReverLoading />}
            </div>
        )

    if (isHoff) {
        return (
            <div className="mb-20 h-fit w-full min-w-[250px] overflow-x-hidden px-2 sm:min-w-[280px]">
                <div
                    id="productPreview"
                    className="mt-4 flex max-w-full  border-solid border-grey-3 py-4 pl-3"
                >
                    <p className="font-haas text-xl font-light">
                        {product.name}
                    </p>
                    {' - '}
                    <p className="font-haas text-xl font-light">
                        {product.variant_name}
                    </p>
                </div>
                <p className="mb-5 mt-4 px-4 font-haas md:px-6">
                    <span
                        className="font-haas font-normal"
                        dangerouslySetInnerHTML={{
                            __html: translate('item_modal_moderate_title')
                        }}
                    />
                </p>
                <div className="mx-4 mt-6 flex flex-col border-t-1 border-black">
                    {isAvailableForExchange && (
                        <button
                            onClick={handleClickExchange}
                            id="exchangeButton"
                        >
                            <p className="flex cursor-pointer items-center gap-2 border-b-1 border-solid border-black px-2 py-4 text-left font-haas font-normal text-black underline-offset-4  hover:bg-neutral-100">
                                <span
                                    className="font-haas"
                                    dangerouslySetInnerHTML={{
                                        __html: translate(
                                            'item_modal_moderate_exchange'
                                        )
                                    }}
                                />
                                <p className="rounded-md bg-black px-2 py-1 font-haas text-xs text-white">
                                    FREE
                                </p>
                            </p>
                        </button>
                    )}
                    {isFullCatalog ? (
                        <button
                            onClick={handleClickFullCatalog}
                            id="exploreButton"
                        >
                            <p className="flex cursor-pointer items-center gap-2 border-b-1 border-solid border-black px-2 py-4 text-left font-haas font-normal text-black underline-offset-4 hover:bg-neutral-100">
                                <span className="mb-1">
                                    <svg
                                        aria-hidden="true"
                                        fill="none"
                                        focusable="false"
                                        width="24"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            d="M8 12V7C8 5.93913 8.42143 4.92172 9.17157 4.17157C9.92172 3.42143 10.9391 3 12 3C13.0609 3 14.0783 3.42143 14.8284 4.17157C15.5786 4.92172 16 5.93913 16 7V12M4 21H20C21.1046 21 22 20.1046 22 19V11C22 9.89543 21.1046 9 20 9H4C2.89543 9 2 9.89543 2 11V19C2 20.1046 2.89543 21 4 21Z"
                                            stroke="currentColor"
                                            strokeWidth="1"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                    </svg>
                                </span>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: translate(
                                            'change_product_full_catalog'
                                        )
                                    }}
                                    className="font-haas text-black"
                                />
                                <p className="rounded-md bg-black px-2 py-1 font-haas text-xs text-white">
                                    FREE
                                </p>
                            </p>
                        </button>
                    ) : null}
                    {returnAvailable ? (
                        <button onClick={handleClickRefund}>
                            <p
                                className="cursor-pointer border-b-1 border-solid border-black px-2 py-4 text-left font-haas font-normal text-black underline-offset-4  hover:bg-neutral-100"
                                dangerouslySetInnerHTML={{
                                    __html: translate(
                                        'item_modal_moderate_refund'
                                    )
                                }}
                            ></p>
                        </button>
                    ) : null}
                </div>
            </div>
        )
    }

    if (isMisako) {
        return (
            <div className="h-full px-4 pb-10 md:min-w-[500px]">
                <p
                    className="mt-6 text-left text-sm font-[300] tracking-wider [font-family:Inter]"
                    dangerouslySetInnerHTML={{
                        __html: translate('item_modal_moderate_title')
                    }}
                />
                <div
                    id="productPreview"
                    className="mt-2 flex max-w-full border-b border-solid border-grey-3 py-4"
                >
                    <ProductPreview product={product} isFlow />
                </div>
                <div className="mt-6 flex flex-col gap-4">
                    {isAvailableForExchange && (
                        <div>
                            <Button
                                fullWidth
                                onClick={handleClickExchange}
                                id="exchangeButton"
                            >
                                <p
                                    className="text-[13px] font-[300] uppercase tracking-wider [font-family:Inter]"
                                    dangerouslySetInnerHTML={{
                                        __html: translate(
                                            'item_modal_moderate_exchange'
                                        )
                                    }}
                                />
                            </Button>
                        </div>
                    )}
                    {isFullCatalog ? (
                        <div>
                            <Button
                                fullWidth
                                onClick={handleClickFullCatalog}
                                id="exploreButton"
                            >
                                <p
                                    className="text-[13px] font-[300] uppercase tracking-wider [font-family:Inter]"
                                    dangerouslySetInnerHTML={{
                                        __html: translate(
                                            'change_product_full_catalog'
                                        )
                                    }}
                                />
                            </Button>
                        </div>
                    ) : null}
                    {returnAvailable ? (
                        <p
                            className="mx-auto mt-3 cursor-pointer text-[13px] font-[300] uppercase tracking-wider text-grey-1 underline underline-offset-8 [font-family:Inter]"
                            onClick={handleClickRefund}
                            dangerouslySetInnerHTML={{
                                __html: translate('item_modal_moderate_refund')
                            }}
                        />
                    ) : null}
                </div>
            </div>
        )
    }

    return (
        <div className="h-fit w-full min-w-[250px] overflow-x-hidden px-2 sm:min-w-[280px]">
            <p className="mt-4 px-4 text-center md:px-6">
                <b
                    dangerouslySetInnerHTML={{
                        __html: translate('item_modal_moderate_title')
                    }}
                />
            </p>
            <div
                id="productPreview"
                className="mt-4 flex max-w-full border-b border-solid border-grey-3 py-4"
            >
                <ProductPreview product={product} />
            </div>
            <ModerateDiv>
                {isAvailableForExchange && (
                    <div>
                        <Button
                            fullWidth
                            onClick={handleClickExchange}
                            id="exchangeButton"
                        >
                            <p
                                dangerouslySetInnerHTML={{
                                    __html: translate(
                                        'item_modal_moderate_exchange'
                                    )
                                }}
                            />
                        </Button>
                    </div>
                )}
                {isFullCatalog ? (
                    <div className="mt-5">
                        <Button
                            fullWidth
                            onClick={handleClickFullCatalog}
                            id="exploreButton"
                        >
                            <p
                                dangerouslySetInnerHTML={{
                                    __html: translate(
                                        'change_product_full_catalog'
                                    )
                                }}
                            />
                        </Button>
                    </div>
                ) : null}
                {returnAvailable ? (
                    <p
                        className="mb-8 mt-4 cursor-pointer text-grey-1 underline underline-offset-4"
                        onClick={handleClickRefund}
                        dangerouslySetInnerHTML={{
                            __html: translate('item_modal_moderate_refund')
                        }}
                    />
                ) : null}
            </ModerateDiv>
        </div>
    )
}

const ModerateDiv = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    margin-top: 2rem;
    margin-bottom: 1rem;
`
